import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';

import {
  getV2ApiCompanyIdAccountingIntegrationsMappingAnalyticalFieldsConfiguration,
  putV2ApiCompanyIdAccountingIntegrationsMappingAnalyticalFieldsConfiguration,
} from 'src/client/spendesk-api';
import { type GetV2ApiCompanyIdAccountingIntegrationsMappingAnalyticalFieldsConfigurationResponseOK } from 'src/client/spendesk-api-types';

import { useCompanyId } from '../../app/hooks/useCompanyId';

type Unpacked<T> = T extends (infer U)[] ? U : T;
export type AnalyticalFieldExportSettings =
  Unpacked<GetV2ApiCompanyIdAccountingIntegrationsMappingAnalyticalFieldsConfigurationResponseOK>;

export const useGetAnalyticalFieldExportSettings = ({
  isEnabled,
}: {
  isEnabled: boolean;
}) => {
  const companyId = useCompanyId();
  const { isLoading, data } = useQuery({
    queryKey: ['exportSettings', companyId],
    staleTime: 60_000,
    enabled: isEnabled,
    refetchOnWindowFocus: false,
    queryFn: async () => {
      const response =
        await getV2ApiCompanyIdAccountingIntegrationsMappingAnalyticalFieldsConfiguration(
          {
            path: {
              companyId,
            },
          },
        );

      if (response.statusCode === 200) {
        return response.body.map(
          (config): AnalyticalFieldExportSettings => config,
        );
      }

      return [];
    },
  });

  const exportConfig = useMemo(() => data ?? [], [data]);

  return {
    isLoading,
    exportConfig,
  };
};

export const usePutAnalyticalFieldExportSettings = () => {
  const companyId = useCompanyId();
  const invalidateQuery = useInvalidateAnalyticalFieldExportSettingsQuery();
  const { mutate, mutateAsync, isLoading } = useMutation({
    mutationFn: async (configurations: AnalyticalFieldExportSettings[]) => {
      const response =
        await putV2ApiCompanyIdAccountingIntegrationsMappingAnalyticalFieldsConfiguration(
          {
            path: {
              companyId,
            },
            body: configurations,
          },
        );

      if (response.statusCode === 200) {
        return response.body;
      }

      return [];
    },
    onSuccess: () => {
      invalidateQuery();
    },
  });

  return {
    updateCompanyExportSettings: mutate,
    updateCompanyExportSettingsAsync: mutateAsync,
    isPending: isLoading,
  };
};

export const useInvalidateAnalyticalFieldExportSettingsQuery = () => {
  const queryClient = useQueryClient();
  const companyId = useCompanyId();

  return () => {
    queryClient.invalidateQueries({
      queryKey: ['exportSettings', companyId],
    });
  };
};
